import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useDialog } from '../context/DialogContext';
import { useLocale } from '../context/LocaleContext';
import { apiService, ShareDocument } from '../services/api';
import { SponsorService, Sponsor } from '../services/sponsorService';
import { formatNameFirstLastInitial } from '../utils/userState';
import { isMobile } from '../utils/mobileDetection';
import { t as translate } from '../i18n/translate';
import styles from '../styles/shareModal.module.css';

export interface StepShareDialogProps {
  isOpen: boolean;
  onClose: () => void;
  user?: any;
  stepTitle: string;
  shareName: string;
  documentType: string;
  generateHtml: () => string;
  canGenerate: () => boolean;
  emptyMessage?: string;
}

type ViewState = 'generating' | 'ready' | 'idle';

function prefersInAppDocumentPreview(): boolean {
  if (typeof window === 'undefined') return false;
  return isMobile() || window.matchMedia('(max-width: 768px)').matches;
}

/** Sticky bar for print preview tabs so users can close and return. */
function wrapHtmlWithBackToolbar(html: string): string {
  const toolbar = `
    <div id="rc-preview-toolbar" style="position:sticky;top:0;z-index:9999;background:#1e3a5f;color:#fff;padding:12px 16px;display:flex;align-items:center;justify-content:space-between;gap:12px;font-family:system-ui,-apple-system,sans-serif;font-size:16px;box-shadow:0 2px 8px rgba(0,0,0,.15);">
      <button type="button" onclick="window.close()" style="background:#fff;color:#1e3a5f;border:none;border-radius:6px;padding:8px 14px;font-size:15px;font-weight:600;cursor:pointer;white-space:nowrap;">${translate('step.share.preview_back')}</button>
      <span style="font-size:13px;opacity:.9;text-align:right;">${translate('step.share.preview_close_tab')}</span>
    </div>`;
  if (html.includes('<body>')) {
    return html.replace('<body>', `<body>${toolbar}`);
  }
  return html;
}

const StepShareDialog: React.FC<StepShareDialogProps> = ({
  isOpen,
  onClose,
  user,
  stepTitle,
  shareName,
  documentType,
  generateHtml,
  canGenerate,
  emptyMessage,
}) => {
  const { showAlert } = useDialog();
  const { t } = useLocale();
  const [viewState, setViewState] = useState<ViewState>('idle');
  const [currentDocument, setCurrentDocument] = useState<ShareDocument | null>(null);
  const [htmlContent, setHtmlContent] = useState<string>('');
  const [justCopiedId, setJustCopiedId] = useState<string | null>(null);
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [sponsors, setSponsors] = useState<Sponsor[]>([]);
  const [selectedSponsorId, setSelectedSponsorId] = useState<string>('');
  const [customEmail, setCustomEmail] = useState('');
  const [emailOption, setEmailOption] = useState<'sponsor' | 'custom' | 'self'>('sponsor');
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const generatingRef = useRef(false);
  const [inAppPreview, setInAppPreview] = useState<{ open: boolean; autoPrint: boolean }>({
    open: false,
    autoPrint: false,
  });
  const previewFrameRef = useRef<HTMLIFrameElement>(null);
  const useInAppPreview = prefersInAppDocumentPreview();

  const loadSponsors = async () => {
    try {
      const sponsorList = await SponsorService.getSponsors();
      setSponsors(sponsorList);
      if (sponsorList.length > 0) {
        setSelectedSponsorId(sponsorList[0].id);
        setEmailOption('sponsor');
      } else {
        setEmailOption('custom');
      }
    } catch {
      setSponsors([]);
      setEmailOption('custom');
    }
  };

  const resetState = () => {
    setViewState('idle');
    setCurrentDocument(null);
    setHtmlContent('');
    setShowEmailForm(false);
    setCustomEmail('');
    setJustCopiedId(null);
    generatingRef.current = false;
    setInAppPreview({ open: false, autoPrint: false });
  };

  const generateDocument = useCallback(async () => {
    if (generatingRef.current) return;
    if (!canGenerate()) {
      showAlert({ message: emptyMessage ?? t('step.share.empty_default'), variant: 'info' });
      onClose();
      return;
    }

    generatingRef.current = true;
    setViewState('generating');

    try {
      const html = generateHtml();
      setHtmlContent(html);

      const result = await apiService.createShareDocument({
        share_name: shareName,
        document_type: documentType,
        html_content: html,
      });

      const shortUrl = `${window.location.origin}/s/${result.document_id}`;
      const shareDocument: ShareDocument = {
        id: result.document_id,
        user_id: user?.email || '',
        share_name: shareName,
        document_type: documentType,
        generation_date: new Date().toISOString(),
        share_url: shortUrl,
        expire_date: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toLocaleDateString('en-US'),
        created_by_user_id: user?.email,
        created_by_name: user?.name,
      };

      setCurrentDocument(shareDocument);
      setViewState('ready');
    } catch (error) {
      console.error('Error generating step share document:', error);
      showAlert({
        message: t('step.share.error_generating'),
        variant: 'danger',
      });
      onClose();
    } finally {
      generatingRef.current = false;
    }
  }, [canGenerate, emptyMessage, generateHtml, shareName, documentType, user, showAlert, onClose, t]);

  useEffect(() => {
    if (isOpen) {
      loadSponsors();
      generateDocument();
    } else {
      resetState();
    }
  }, [isOpen]);

  const openHtmlInWindow = (autoPrint: boolean) => {
    if (!htmlContent) return;
    const win = window.open('about:blank', '_blank');
    if (!win) {
      showAlert({ message: t('step.share.popup_blocked'), variant: 'info' });
      return;
    }
    win.document.write(wrapHtmlWithBackToolbar(htmlContent));
    win.document.close();
    if (autoPrint) {
      win.onload = () => {
        win.focus();
        win.print();
      };
    }
  };

  const openDocumentPreview = (autoPrint: boolean) => {
    if (!htmlContent) return;
    if (useInAppPreview) {
      setInAppPreview({ open: true, autoPrint });
      return;
    }
    openHtmlInWindow(autoPrint);
  };

  const closeInAppPreview = () => {
    setInAppPreview({ open: false, autoPrint: false });
  };

  const printFromInAppPreview = () => {
    const frame = previewFrameRef.current;
    if (!frame?.contentWindow) return;
    try {
      frame.contentWindow.focus();
      frame.contentWindow.print();
    } catch {
      showAlert({ message: t('step.share.print_failed'), variant: 'info' });
    }
  };

  useEffect(() => {
    if (!inAppPreview.open || !inAppPreview.autoPrint) return;
    const frame = previewFrameRef.current;
    if (!frame) return;

    const triggerPrint = () => {
      window.setTimeout(() => {
        try {
          frame.contentWindow?.focus();
          frame.contentWindow?.print();
        } catch {
          /* print may be blocked; user can tap Print in toolbar */
        }
      }, 400);
    };

    frame.addEventListener('load', triggerPrint);
    return () => frame.removeEventListener('load', triggerPrint);
  }, [inAppPreview.open, inAppPreview.autoPrint]);

  const handlePreview = () => {
    if (currentDocument && !useInAppPreview) {
      window.open(currentDocument.share_url, '_blank', 'noopener,noreferrer');
      return;
    }
    openDocumentPreview(false);
  };

  const handlePrint = () => openDocumentPreview(true);

  const handleCopyLink = async () => {
    if (!currentDocument) return;
    try {
      await navigator.clipboard.writeText(currentDocument.share_url);
      setJustCopiedId(currentDocument.id);
      setTimeout(() => setJustCopiedId(null), 3000);
    } catch {
      showAlert({ message: t('step.share.copy_failed'), variant: 'danger' });
    }
  };

  const isValidEmail = (email: string): boolean => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const userEmail = (user?.email || '').trim().toLowerCase();
  const customEmailNorm = customEmail.trim().toLowerCase();
  const recipientIsSelf =
    emailOption === 'self' || (emailOption === 'custom' && customEmailNorm !== '' && customEmailNorm === userEmail);
  const showCopiedNote = !recipientIsSelf && Boolean(userEmail);

  const canSendEmail = (): boolean => {
    if (emailOption === 'self') return Boolean(userEmail);
    if (emailOption === 'sponsor') return selectedSponsorId !== '';
    return customEmail !== '' && isValidEmail(customEmail);
  };

  const handleSendEmail = async () => {
    if (!currentDocument || !canSendEmail()) return;

    setIsSendingEmail(true);
    try {
      let recipientEmail = '';
      let recipientName = '';

      if (emailOption === 'self') {
        recipientEmail = user?.email || '';
      } else if (emailOption === 'sponsor' && selectedSponsorId) {
        const sponsor = sponsors.find((s) => s.id === selectedSponsorId);
        if (sponsor) {
          recipientEmail = sponsor.email;
          recipientName = sponsor.name;
        }
      } else if (emailOption === 'custom') {
        recipientEmail = customEmail.trim();
      }

      if (!recipientEmail) throw new Error('No recipient email');

      await apiService.sendSnapshotEmail({
        document_id: currentDocument.id,
        recipient_email: recipientEmail,
        recipient_name: recipientName,
      });

      const successMessage =
        emailOption === 'self'
          ? t('step.share.email_sent_self', { email: recipientEmail })
          : t('step.share.email_sent', { email: recipientEmail });

      showAlert({
        message: successMessage,
        variant: 'success',
      });
      setShowEmailForm(false);
      setCustomEmail('');
    } catch (error) {
      console.error('Error sending email:', error);
      showAlert({ message: t('step.share.email_failed'), variant: 'danger' });
    } finally {
      setIsSendingEmail(false);
    }
  };

  const handleModalClose = () => {
    resetState();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className={styles.modalOverlay} onClick={handleModalClose}>
      <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <div className={styles.titleSection}>
            <h2 className={styles.modalTitle}>{t('step.share.title')}</h2>
            <div className={styles.modalSubtitle}>{stepTitle}</div>
          </div>
          <button type="button" className={styles.closeButton} onClick={handleModalClose} aria-label={t('step.share.close')}>
            ×
          </button>
        </div>

        <div className={styles.modalBody}>
          {viewState === 'generating' && (
            <div className={styles.generatingContainer}>
              <div className={styles.spinner} />
              <p className={styles.generatingText}>{t('step.share.generating')}</p>
              <p className={styles.generatingSubtext}>{t('step.share.generating_subtext')}</p>
            </div>
          )}

          {viewState === 'ready' && currentDocument && (
            <div className={styles.readyContainer}>
              <div className={styles.successMessage}>{t('step.share.ready')}</div>

              <button type="button" className={styles.previewButton} onClick={handlePreview}>
                {t('step.share.view_button')}
              </button>

              <div className={styles.shareOptionsTitle}>{t('step.share.options_title')}</div>
              <p className={styles.shareOptionsHint}>
                {useInAppPreview
                  ? t('step.share.options_hint_in_app')
                  : t('step.share.options_hint_window')}
              </p>

              <div className={styles.shareButtonsGrid}>
                <button
                  type="button"
                  className={`${styles.shareButton} ${justCopiedId === currentDocument.id ? styles.copiedButton : ''}`}
                  onClick={handleCopyLink}
                >
                  <span className={styles.shareIcon}>{justCopiedId === currentDocument.id ? '✓' : '📋'}</span>
                  <span className={styles.shareButtonLabel}>
                    {justCopiedId === currentDocument.id ? t('step.share.copied') : t('step.share.copy_link')}
                  </span>
                </button>

                <button
                  type="button"
                  className={`${styles.shareButton} ${showEmailForm ? styles.activeButton : ''}`}
                  onClick={() => setShowEmailForm(!showEmailForm)}
                >
                  <span className={styles.shareIcon}>✉️</span>
                  <span className={styles.shareButtonLabel}>{t('step.share.email')}</span>
                  <span className={styles.shareButtonHint}>{t('step.share.email_hint')}</span>
                </button>

                <button type="button" className={styles.shareButton} onClick={handlePrint}>
                  <span className={styles.shareIcon}>🖨️</span>
                  <span className={styles.shareButtonLabel}>{t('step.share.print')}</span>
                  <span className={styles.shareButtonHint}>
                    {useInAppPreview ? t('step.share.print_hint_in_app') : t('step.share.print_hint_window')}
                  </span>
                </button>
              </div>

              {showEmailForm && (
                <div className={styles.emailFormContainer}>
                  <h4 className={styles.emailFormTitle}>{t('step.share.email_form_title')}</h4>

                  {sponsors.length > 0 && (
                    <div className={styles.emailField}>
                      <label className={styles.emailRadioLabel}>
                        <input
                          type="radio"
                          name="stepShareEmailOption"
                          checked={emailOption === 'sponsor'}
                          onChange={() => setEmailOption('sponsor')}
                          className={styles.emailRadio}
                        />
                        <span>{t('step.share.email_to_sponsor')}</span>
                      </label>
                      <select
                        value={selectedSponsorId}
                        onChange={(e) => {
                          setSelectedSponsorId(e.target.value);
                          setEmailOption('sponsor');
                        }}
                        className={styles.emailSelect}
                      >
                        <option value="">{t('step.share.select_sponsor')}</option>
                        {sponsors.map((sponsor) => (
                          <option key={sponsor.id} value={sponsor.id}>
                            {formatNameFirstLastInitial(sponsor.name, sponsor.name)} ({sponsor.email})
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  <div className={styles.emailField}>
                    <label className={styles.emailRadioLabel}>
                      <input
                        type="radio"
                        name="stepShareEmailOption"
                        checked={emailOption === 'custom'}
                        onChange={() => setEmailOption('custom')}
                        className={styles.emailRadio}
                      />
                      <span>{t('step.share.email_to_other')}</span>
                    </label>
                    <input
                      type="email"
                      placeholder={t('step.share.email_placeholder')}
                      value={customEmail}
                      onChange={(e) => {
                        setCustomEmail(e.target.value);
                        if (e.target.value.length > 0) setEmailOption('custom');
                      }}
                      className={styles.emailInput}
                    />
                    {customEmail && !isValidEmail(customEmail) && (
                      <div className={styles.validationError}>{t('step.share.email_invalid')}</div>
                    )}
                  </div>

                  {userEmail && (
                    <div className={styles.emailField}>
                      <label className={styles.emailRadioLabel}>
                        <input
                          type="radio"
                          name="stepShareEmailOption"
                          checked={emailOption === 'self'}
                          onChange={() => setEmailOption('self')}
                          className={styles.emailRadio}
                        />
                        <span>{t('step.share.email_to_self')}</span>
                      </label>
                      <p className={styles.emailFieldHint}>{user?.email}</p>
                    </div>
                  )}

                  {showCopiedNote && (
                    <div className={styles.emailCopyMeSection}>
                      <p className={styles.emailFieldHint} style={{ margin: 0 }}>
                        {t('step.share.copied_note')}
                      </p>
                    </div>
                  )}

                  <div className={styles.emailFormButtons}>
                    <button
                      type="button"
                      className={styles.emailCancelButton}
                      onClick={() => {
                        setShowEmailForm(false);
                        setCustomEmail('');
                      }}
                      disabled={isSendingEmail}
                    >
                      {t('step.share.cancel')}
                    </button>
                    <button
                      type="button"
                      className={styles.emailSendButton}
                      onClick={handleSendEmail}
                      disabled={!canSendEmail() || isSendingEmail}
                    >
                      {isSendingEmail ? t('step.share.sending') : t('step.share.send_email')}
                    </button>
                  </div>
                </div>
              )}

              <p style={{ fontSize: '0.85rem', color: '#6b7280', marginTop: '1rem' }}>
                {t('step.share.expiry_note', { date: currentDocument.expire_date })}
              </p>
            </div>
          )}
        </div>
      </div>

      {inAppPreview.open && htmlContent && (
        <div className={styles.inAppPreviewShell} role="dialog" aria-modal="true" aria-label={t('step.share.preview_aria')}>
          <div className={styles.inAppPreviewToolbar}>
            <button type="button" className={styles.inAppPreviewDone} onClick={closeInAppPreview}>
              {t('step.share.preview_done')}
            </button>
            <span className={styles.inAppPreviewTitle}>{t('step.share.preview_title')}</span>
            <button type="button" className={styles.inAppPreviewPrint} onClick={printFromInAppPreview}>
              {t('step.share.print')}
            </button>
          </div>
          <p className={styles.inAppPreviewHint}>{t('step.share.preview_hint')}</p>
          <iframe
            ref={previewFrameRef}
            className={styles.inAppPreviewFrame}
            srcDoc={htmlContent}
            title={t('step.share.preview_title')}
          />
        </div>
      )}
    </div>
  );
};

export default StepShareDialog;
